/**
 * THE FILE — the composer. Where work enters the operating system.
 *
 * One bordered box holds everything a person says when they hand over a piece
 * of work: the sentence, the files they have in hand, and the context it
 * belongs to. They are in ONE region because they are one act — proximity is
 * the strongest grouping the page has, and a context question set apart under
 * the box read as a second form to fill in.
 *
 * The box is a separable object (a request being written), so it has an edge
 * and no radius. When the writing area inside it has focus, the BOX takes the
 * ring: a ring around the textarea alone would draw a box inside a box.
 */
import { clsx } from "clsx";
import type { ReactNode } from "react";

export interface ComposerFrameProps {
  /** True while files are being dragged over the page. The only state the frame has. */
  dragging?: boolean | undefined;
  children: ReactNode;
  className?: string | undefined;
}

export function ComposerFrame({ dragging, children, className }: ComposerFrameProps) {
  return (
    <div
      className={clsx(
        "flex flex-col gap-3.5 border bg-raised px-4 pb-3 pt-4 transition-colors duration-150 sm:px-[18px]",
        dragging ? "border-forest outline outline-1 outline-forest" : "border-line-strong",
        "has-[textarea:focus]:border-forest has-[textarea:focus]:outline has-[textarea:focus]:outline-1 has-[textarea:focus]:outline-forest",
        className
      )}
    >
      {children}
    </div>
  );
}

export interface ComposerBarProps {
  /** Left: what the work is FOR — the context chips. */
  context: ReactNode;
  /** Right: the means of input, and the one primary action, last. */
  actions: ReactNode;
  /** One quiet line: the next thing to do, and only that. */
  note?: ReactNode;
}

/**
 * The bar along the bottom of the box. Context on the left, actions on the
 * right — serial position puts the commitment last. The note sits under the
 * bar at a desk and between the chips and the actions on a phone, so it is
 * always read before the button it explains. On a phone the actions take a
 * row of their own and the primary widens to fill it.
 */
export function ComposerBar({ context, actions, note }: ComposerBarProps) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2.5 border-t border-rule-soft pt-3">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{context}</div>
      {note && <p className="basis-full text-[12px] text-stone sm:order-last sm:-mt-1">{note}</p>}
      <div className="ml-auto flex items-center gap-1.5 max-sm:w-full">{actions}</div>
    </div>
  );
}
